import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";
import { useNavigate } from "react-router-dom";

interface Test {
    id: number;
    name: string;
    description?: string;
    is_published: boolean;
    is_active: boolean;
    created_utc: string;
    updated_utc?: string;
    created_by?: string;
    organization_id?: number;
}

export default function TestsPage() {
    const [tests, setTests] = useState<Test[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeDropdown, setActiveDropdown] = useState<number | null>(null);

    const nav = useNavigate();

    // 🔹 Load tests
    useEffect(() => {
        async function loadTests() {
            try {
                const { data, error } = await supabase
                    .from("tests")
                    .select("id, name, description, is_published, is_active, created_utc, updated_utc, created_by, organization_id")
                    .order("created_utc", { ascending: false });

                if (error) throw error;

                setTests(data || []);
            } catch (err: any) {
                console.error("Error loading tests:", err.message);
                setError("❌ Failed to load tests");
            } finally {
                setLoading(false);
            }
        }

        loadTests();
    }, []);

    // 🔹 Actions
    function handlePreview(testId: number) {
        nav(`/tests/${testId}/preview`);
    }

    function handleEdit(testId: number) {
        nav(`/tests/${testId}/edit`);
    }

    async function handleDelete(testId: number) {
        if (!confirm("Are you sure you want to delete this test?")) return;

        const { error } = await supabase.from("tests").delete().eq("id", testId);

        if (error) {
            alert("❌ Failed to delete test: " + error.message);
        } else {
            alert("✅ Test deleted successfully");
            setTests((prev) => prev.filter((t) => t.id !== testId));
        }
    }

    // 🔹 UI
    if (loading) return <p className="p-6">Loading tests...</p>;
    if (error) return <p className="p-6 text-red-600">{error}</p>;

    return (
        <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4">Tests</h2>

            {tests.length === 0 ? (
                <p className="text-gray-600">No tests found.</p>
            ) : (
                <table className="w-full border text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-left">
                            <th className="p-2 border">ID</th>
                            <th className="p-2 border">Name</th>
                            <th className="p-2 border">Description</th>
                            <th className="p-2 border">Published</th>
                            <th className="p-2 border">Active</th>
                            <th className="p-2 border">Created</th>
                            <th className="p-2 border">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tests.map((test) => (
                            <tr key={test.id}>
                                <td className="p-2 border">{test.id}</td>
                                <td className="p-2 border">{test.name}</td>
                                <td className="p-2 border">{test.description || "-"}</td>
                                <td className="p-2 border">{test.is_published ? "✅ Yes" : "❌ No"}</td>
                                <td className="p-2 border">{test.is_active ? "✅ Yes" : "❌ No"}</td>
                                <td className="p-2 border">
                                    {test.created_utc ? new Date(test.created_utc).toLocaleDateString() : "-"}
                                </td>
                                <td className="p-2 border text-center relative">
                                    {/* Dropdown */}
                                    <button
                                        className="bg-blue-500 text-white px-3 py-1 rounded"
                                        onClick={() =>
                                            setActiveDropdown(activeDropdown === test.id ? null : test.id)
                                        }
                                    >
                                        Actions ▾
                                    </button>

                                    {activeDropdown === test.id && (
                                        <div className="absolute right-0 mt-2 w-32 bg-white border rounded shadow-lg z-10">
                                            <button
                                                onClick={() => handlePreview(test.id)}
                                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            >
                                                Preview
                                            </button>
                                            <button
                                                onClick={() => handleEdit(test.id)}
                                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(test.id)}
                                                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
